import React, { useState, useEffect, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, Truck, Mail, ShoppingCart, Plus, Minus, Store } from 'lucide-react';
import { useCart } from '@/lib/CartContext';
import ProductDescription from '@/components/shop/ProductDescription';
import ShareButtons from '@/components/shop/ShareButtons';
import ProductReviews, { ProductRatingBadge } from '@/components/shop/ProductReviews';
import { useSeo } from '@/lib/seo';
import { trackViewItem, trackAddToCart } from '@/lib/analytics';
import { useCompare, toggleCompare, addRecentlyViewed, deriveBrand } from '@/lib/productStore';
import { useCategoryLabels } from '@/hooks/useCategoryLabels';
import RecentlyViewed from '@/components/shop/RecentlyViewed';
import DeliveryExpectations from '@/components/shop/DeliveryExpectations';
import { weightToGrams, findShippingRate } from '@/lib/shipping';
import { Scale } from 'lucide-react';

export default function ProductDetail() {
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const categoryLabel = useCategoryLabels();
  const routeParam = window.location.pathname.split('/').pop();
  const norm = (s) => (s || '').toLowerCase().replace(/^\/+|\/+$/g, '');
  const paramNorm = norm(routeParam);

  const { data: product, isLoading } = useQuery({
    queryKey: ['product', routeParam],
    queryFn: async () => {
      const products = await base44.entities.Product.list();
      let p = products.find((x) => x.id === routeParam);
      if (!p && paramNorm) p = products.find((x) => x.custom_slug && norm(x.custom_slug) === paramNorm);
      if (!p && paramNorm) p = products.find((x) => x.sku && norm(x.sku) === paramNorm);
      return p;
    },
  });

  // Load variants if product is a parent item
  const { data: variants = [] } = useQuery({
    queryKey: ['product-variants', product?.square_item_id],
    queryFn: async () => {
      if (!product?.square_item_id) return [];
      const products = await base44.entities.Product.list();
      return products.filter(p => p.square_parent_item_id === product.square_item_id && p.is_variant);
    },
    enabled: !!product?.square_item_id,
  });

  // If current product is a variant, use it directly; otherwise select first variant or the product itself
  const [selectedVariant, setSelectedVariant] = useState(null);
  const displayProduct = selectedVariant || product;
  const compare = useCompare();
  const isCompared = displayProduct ? compare.includes(displayProduct.id) : false;

  // Show "Standard Black" first on every product, then any other colour options.
  const sortedVariants = [...variants].sort((a, b) => {
    const la = (a.variation_name || a.option_value || a.name || '').toLowerCase();
    const lb = (b.variation_name || b.option_value || b.name || '').toLowerCase();
    const aBlack = la.includes('black');
    const bBlack = lb.includes('black');
    if (aBlack !== bBlack) return aBlack ? -1 : 1;
    return 0;
  });

  useEffect(() => {
    if (product && sortedVariants.length > 0 && !selectedVariant) {
      setSelectedVariant(sortedVariants[0]);
    }
  }, [product, sortedVariants, selectedVariant]);

  // Approved on-site reviews tied to this product (for Product structured data).
  const { data: productReviews = [] } = useQuery({
    queryKey: ['product-reviews', product?.id],
    queryFn: async () => {
      if (!product?.id) return [];
      const all = await base44.entities.Review.filter({ product_id: product.id, status: 'approved' }, '-created_date', 50);
      return (all || []).filter((r) => r.review_category === 'products');
    },
    enabled: !!product?.id,
  });

  const approvedReviews = productReviews.filter((r) => r.rating >= 1 && r.rating <= 5);
  const ratingCount = approvedReviews.length;
  const avgRating = ratingCount > 0 ? approvedReviews.reduce((s, r) => s + Number(r.rating), 0) / ratingCount : 0;

  const { data: shippingRates = [] } = useQuery({
    queryKey: ['shipping-rates-pdp'],
    queryFn: () => base44.entities.ShippingRate.list(),
  });
  const productShippingRate = useMemo(() => {
    if (!displayProduct || !shippingRates.length) return null;
    const grams = weightToGrams(displayProduct.weight, displayProduct.weight_unit);
    return findShippingRate(shippingRates, grams);
  }, [displayProduct, shippingRates]);

  useSeo({
    title: displayProduct?.seo_title || displayProduct?.name,
    description: displayProduct?.seo_description || (displayProduct?.description ? displayProduct.description.slice(0, 160) : undefined),
    image: displayProduct?.image_url,
    type: 'product',
    jsonLd: displayProduct ? {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: displayProduct.name,
      description: displayProduct.seo_description || displayProduct.description || '',
      image: displayProduct.image_url || undefined,
      sku: displayProduct.sku || undefined,
      // Only emit gtin when it's a valid GTIN format (8, 12, 13 or 14 digits) — Google flags anything else as Invalid GTIN.
      ...(displayProduct.gtin && /^[0-9]{8}$|^[0-9]{12}$|^[0-9]{13}$|^[0-9]{14}$/.test(displayProduct.gtin)
        ? { gtin: displayProduct.gtin }
        : {}),
      brand: { '@type': 'Brand', name: deriveBrand(displayProduct) || 'Motion Reality' },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'GBP',
        price: displayProduct.price,
        availability: displayProduct.in_stock === false ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
        url: window.location.href,
        validFrom: displayProduct.created_date || new Date().toISOString(),
        priceValidUntil: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365).toISOString().split('T')[0],
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          applicableCountry: 'GB',
          returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
          merchantReturnDays: 30,
          returnMethod: 'https://schema.org/ReturnByMail',
          returnFees: 'https://schema.org/FreeReturn',
          url: `${window.location.origin}/refund-policy`,
        },
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'GB' },
          ...(productShippingRate ? {
            shippingRate: {
              '@type': 'MonetaryAmount',
              value: Number(productShippingRate.rate).toFixed(2),
              currency: 'GBP',
            },
          } : {}),
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 1, unitCode: 'DAY' },
            transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 2, unitCode: 'DAY' },
          },
        },
      },
      ...(ratingCount > 0
        ? {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: Number(avgRating.toFixed(1)),
              reviewCount: ratingCount,
              bestRating: 5,
              worstRating: 1,
            },
            review: approvedReviews.slice(0, 6).map((r) => ({
              '@type': 'Review',
              author: { '@type': 'Person', name: r.author_name || 'Verified Customer' },
              datePublished: r.created_date ? r.created_date.split('T')[0] : undefined,
              reviewRating: { '@type': 'Rating', ratingValue: Number(r.rating), bestRating: 5, worstRating: 1 },
              reviewBody: r.quote || undefined,
            })),
          }
        : {}),
    } : undefined,
  });

  useEffect(() => {
    if (displayProduct) trackViewItem(displayProduct);
  }, [displayProduct?.id]);

  useEffect(() => {
    if (product?.id) addRecentlyViewed(product.id);
  }, [product?.id]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex flex-col items-center justify-center gap-4">
        <p className="text-white/30 text-sm">Product not found</p>
        <Link to="/shop" className="text-primary text-sm flex items-center gap-2 hover:underline">
          <ArrowLeft className="w-4 h-4" /> Back to Shop
        </Link>
      </div>
    );
  }

  const handleAddToCart = () => {
    trackAddToCart(displayProduct, qty);
    addToCart(displayProduct, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Breadcrumb */}
      <div className="border-b border-white/5 px-6 lg:px-10 py-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/shop" className="inline-flex items-center gap-2 text-white/30 hover:text-white text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Shop
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Image */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <div className="aspect-square bg-[#111115] overflow-hidden relative">
              {displayProduct.image_url ? (
                <img src={displayProduct.image_url} alt={displayProduct.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-white/10 text-xs">NO IMAGE</span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Details */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex flex-col">
            <p className="text-primary text-xs font-bold tracking-widest mb-3 uppercase">
              {categoryLabel(displayProduct.category)}
            </p>
            <h1 className="text-white font-black text-2xl md:text-3xl uppercase leading-tight mb-5">
              {displayProduct.name}
            </h1>

            {ratingCount > 0 && (
              <ProductRatingBadge reviews={approvedReviews} avgRating={avgRating} ratingCount={ratingCount} />
            )}

            {/* Variant selector */}
            {sortedVariants.length > 0 && (
              <div className="mb-6 pb-6 border-b border-white/5">
                <p className="text-white/40 text-xs font-bold tracking-widest mb-3">OPTIONS</p>
                <div className="flex flex-wrap gap-2">
                  {sortedVariants.map((variant) => (
                    <button
                      key={variant.id}
                      onClick={() => setSelectedVariant(variant)}
                      className={`px-4 py-2 text-xs font-bold border transition-all ${
                        selectedVariant?.id === variant.id
                          ? 'bg-primary border-primary text-white'
                          : 'border-white/10 text-white/60 hover:border-white/30 hover:text-white'
                      }`}
                    >
                      {variant.variation_name || variant.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 pb-6 border-b border-white/5 mb-6">
              <p className="text-white font-black text-3xl">£{displayProduct.price?.toFixed(2)}</p>
              {(displayProduct.in_stock === false || (displayProduct.stock_quantity !== null && displayProduct.stock_quantity !== undefined && displayProduct.stock_quantity <= 0)) ? (
                <span className="bg-white/5 text-white/30 text-xs font-bold tracking-wider px-3 py-1">OUT OF STOCK</span>
              ) : displayProduct.stock_quantity != null && displayProduct.stock_quantity <= 3 ? (
                <span className="bg-yellow-500/10 text-yellow-400 text-xs font-bold tracking-wider px-3 py-1">ONLY {displayProduct.stock_quantity} LEFT</span>
              ) : displayProduct.stock_quantity != null ? (
                <span className="bg-emerald-500/10 text-emerald-400 text-xs font-bold tracking-wider px-3 py-1">{displayProduct.stock_quantity} IN STOCK</span>
              ) : (
                <span className="bg-emerald-500/10 text-emerald-400 text-xs font-bold tracking-wider px-3 py-1">IN STOCK</span>
              )}
            </div>

            {displayProduct.description && (
              <div className="mb-6">
                <ProductDescription description={displayProduct.description} />
              </div>
            )}

            {displayProduct.specs && (
              <div className="bg-[#111115] border border-white/5 p-5 mb-6">
                <p className="text-primary text-[10px] font-bold tracking-widest mb-4">SPECIFICATIONS</p>
                <p className="text-white/40 text-sm leading-relaxed whitespace-pre-line">{displayProduct.specs}</p>
              </div>
            )}

            <DeliveryExpectations product={displayProduct} />

            {/* In-store availability — Bromborough collection */}
            <div className="bg-[#111115] border border-primary/20 p-4 mb-8">
              <div className="flex items-center gap-2 mb-2.5">
                <Store className="w-4 h-4 text-primary" />
                <p className="text-white text-[10px] font-bold tracking-widest">IN STORE AT BROMBOROUGH</p>
                <div className="flex-1 h-px bg-white/5" />
              </div>
              <p className="text-white/60 text-xs leading-relaxed mb-2">
                {displayProduct.in_stock === false || (displayProduct.stock_quantity != null && displayProduct.stock_quantity <= 0)
                  ? <>Out of stock at our Bromborough unit — <a href="tel:01515283001" className="text-primary hover:underline font-bold">call us</a> to order.</>
                  : 'In stock and ready to collect from our Bromborough unit.'}
              </p>
              <p className="text-white/30 text-[10px] leading-relaxed">
                <span className="text-white/50 font-bold">Click & Collect available</span> — pay online and pick up at Unit 9 Croft Business Park, Bromborough CH62 3RA. Ready same day if ordered before 2pm, otherwise next working day.
              </p>
            </div>

            <div className="mt-auto space-y-3">
              {(displayProduct.in_stock !== false && !(displayProduct.stock_quantity !== null && displayProduct.stock_quantity !== undefined && displayProduct.stock_quantity <= 0)) ? (
                <>
                  {/* Quantity selector */}
                  <div className="flex items-center gap-3">
                    <p className="text-white/40 text-xs font-bold tracking-widest">QTY</p>
                    <div className="flex items-center gap-0 border border-white/10">
                      <button onClick={() => setQty(q => Math.max(1, q - 1))}
                        className="w-10 h-10 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5 transition-all">
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-white font-bold w-10 text-center">{qty}</span>
                      <button onClick={() => setQty(q => q + 1)}
                        className="w-10 h-10 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5 transition-all">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={handleAddToCart}
                    className={`flex items-center justify-center gap-2 w-full font-bold text-sm py-4 transition-all ${added ? 'bg-green-600 text-white' : 'bg-primary hover:bg-primary/90 text-white'}`}
                  >
                    <ShoppingCart className="w-4 h-4" />
                    {added ? 'ADDED TO CART!' : `ADD TO CART — £${(displayProduct.price * qty)?.toFixed(2)}`}
                  </button>

                </>
              ) : (
                <a
                  href={`mailto:sales@motionreality.co.uk?subject=Order Enquiry: ${displayProduct.name}`}
                  className="flex items-center justify-center gap-2 w-full bg-primary hover:bg-primary/90 text-white font-bold text-sm py-4 transition-colors"
                >
                  <Mail className="w-4 h-4" /> ENQUIRE / ORDER NOW
                </a>
              )}
              <button
                type="button"
                onClick={() => toggleCompare(displayProduct.id)}
                className={`flex items-center justify-center gap-2 w-full font-bold text-sm py-3 transition-all ${isCompared ? 'bg-primary/10 border border-primary/40 text-primary' : 'border border-white/10 hover:border-white/25 text-white/50 hover:text-white'}`}
              >
                <Scale className="w-4 h-4" /> {isCompared ? 'IN COMPARE' : 'ADD TO COMPARE'}
              </button>
            </div>

            <div className="mt-6 pt-6 border-t border-white/5 flex items-center justify-between">
              <p className="text-white/30 text-[10px] font-bold tracking-widest">SHARE</p>
              <ShareButtons title={displayProduct.name} />
            </div>
          </motion.div>
        </div>
      </div>

      <ProductReviews reviews={approvedReviews} avgRating={avgRating} ratingCount={ratingCount} productName={product?.name} />

      <RecentlyViewed excludeId={product?.id} />
    </div>
  );
}